import { Box, FormControl, InputLabel, MenuItem } from '@mui/material';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import React, { FC, useState } from 'react';

import { selectReservationDate } from 'infrastructure/redux/slices/bookingWizard.selector';
import { setLessonType, setReservationDate } from 'infrastructure/redux/slices/bookingWizard.slice';
import { useAppDispatch, useAppSelector } from 'infrastructure/redux/store/hooks';
import SmallDatePicker from 'infrastructure/targets/web/modules/bookingWizard/Steps/SessionLengthStep/components/SmallDatePicker';
import { LESSON_NUM_OF_DAYS_TO_BOOK_IN_ADVANCE } from 'infrastructure/components/BookingCalendar';

import { StyledSelectorCard, StyledSelectorContainer } from './style';

interface IDateAndLessonTypeSelector {
  onDateChange?: (newDate: Date | undefined) => void;
  onLessonTypeChange?: (lessonType: string | undefined) => void;
  lessonTypes?: {
    title: string;
    label: string;
    additionalValue: number;
    value: number;
    subtitle: string | null | undefined;
  }[];
}

const DateAndLessonTypeSelector: FC<IDateAndLessonTypeSelector> = ({
  onDateChange,
  onLessonTypeChange,
  lessonTypes,
}) => {
  const dispatch = useAppDispatch();
  const reservationDate = useAppSelector(selectReservationDate);
  const [lessonType, setLessonTypeState] = useState<string | undefined>('');

  const handleDateChange = (newDate: Date | undefined) => {
    dispatch(setReservationDate({ reservationDate: newDate }));
    if (onDateChange) {
      onDateChange(newDate);
    }
  };

  const handleLessonTypeChange = (event: SelectChangeEvent) => {
    const value = event.target.value;
    setLessonTypeState(value);
    dispatch(setLessonType({ lessonType: value }));
    if (onLessonTypeChange) {
      onLessonTypeChange(value);
    }
  };

  const generateDropdownOptions = () => {
    // filter out swing eval option
    const filteredLessonTypes = lessonTypes?.filter(
      (item) => !item.title.includes('Swing Evaluation'),
    );
    return filteredLessonTypes?.map((item) => (
      <MenuItem key={item.title} value={item.value}>
        {item.title}
      </MenuItem>
    ));
  };

  return (
    <StyledSelectorCard>
      <StyledSelectorContainer>
        <Box
          sx={{
            display: 'inline-flex',
            flex: '1 1 auto',
            gap: 4,
            alignItems: 'center',
            width: { xs: '100%', sm: 'auto' },
          }}>
          <SmallDatePicker
            selectedDate={reservationDate}
            onChange={handleDateChange}
            availableNumberOfDaysToChooseFromToday={LESSON_NUM_OF_DAYS_TO_BOOK_IN_ADVANCE}
          />
        </Box>
        <FormControl sx={{ flex: '1 1 auto', width: { xs: '100%', sm: 'auto' } }}>
          <InputLabel id="lesson_type_select">Lesson Type</InputLabel>
          <Select
            labelId="lesson_type_select"
            sx={{
              '& .MuiSelect-icon': {
                display: 'block',
              },
              minWidth: '200px',
            }}
            id="lesson_type_select"
            name="lessonType"
            value={lessonType?.toString()}
            label="Lesson Type"
            color="secondary"
            onChange={handleLessonTypeChange}>
            {generateDropdownOptions()}
          </Select>
        </FormControl>
      </StyledSelectorContainer>
    </StyledSelectorCard>
  );
};

export default DateAndLessonTypeSelector;
